import { useRouter } from 'next/router';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useAgentName } from '../../../lib/use-agent-name';
import { printDocument } from '../../../lib/doc-print';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { DocViewerKind } from '../../../lib/doc-viewer';
import { useFileSize } from '../../../lib/use-file-size';
import { buildFileActions, type FileActionCallbacks } from '../../../lib/file-actions';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { Modal } from '../../Modal';
import { Button } from '../ui';
import { FileMetaLine, fileSubtitle } from '../file-detail/FileDetailMeta';
import { FileActionButton } from '../file-detail/FileActionButton';
import { DocViewerBody } from './DocViewerBody';
import { docViewerBodyFit } from './body-class';
import styles from './DocViewerModal.module.css';

// The in-app document viewer. EVERY file opens here — a report, a sheet, an image, a video, a PDF, a wixel
// asset, and equally a `.docx` we cannot render, which gets a no-preview state in the same shell rather
// than being handed to the browser or routed to another page. One presentation, one set of actions, so
// "what happens when I click a file" no longer depends on which list you clicked from.
//
// Built on the shared `Modal` shell so it inherits the portal, Escape, scrim, RailCatcher and footer slot,
// plus a `cardClassName` that bounds the card to the viewport (see the module CSS). Not a route: a route
// would put the signed WixMP URL into browser history and the referrer, and the chat surface underneath is
// an overlay the user must not lose.
//
// Layout follows the Figma header/body/footer (node 12811-162388), sized up to the DS `maximum` width so the
// document gets most of the screen: the header is the file name + a `File · built by … · workspace` subtitle
// (each segment shown only when its datum exists), the body is a two-column split — the `DocViewerBody`
// viewer on the left (the reading surface, capped to the viewport with its own internal scroll) beside a
// quiet Kind/Size/Type/Date details column — and the footer carries the utility actions (Download / Print /
// Open in new tab, from the ONE shared `buildFileActions`) on the left with Discuss on the right.
/** This shell's own class for each fit. `framed` (html) hides the second scrollbar since the sandboxed
 *  iframe scrolls itself; `compact` (audio) needs no special box here — the two-column body already sizes it. */
const BODY_FIT: Record<ReturnType<typeof docViewerBodyFit>, string> = {
  fill: '',
  framed: styles.bodyFramed,
  compact: '',
};

export function DocViewerModal({
  file,
  kind,
  pageName,
  fileHref,
  workspaceName,
  onClose,
}: {
  file: WorkspaceFile;
  /** Which viewer to render, RESOLVED BY THE CALLER — never re-derived here.
   *
   *  The call sites classify with three inputs (name, url, mimeType); a `WorkspaceFile` carries only
   *  name + content, so re-deriving inside would drop the mime and answer `null` for exactly the
   *  attachment the mime rescue exists for (no name extension, no URL extension, `mimeType: text/html`) —
   *  a chip that suppressed the browser hand-off and then rendered nothing at all. The caller already
   *  knows the answer, so it passes it; the alternative (smuggling a `mimeType` onto `WorkspaceFile`)
   *  would put a field on the workspace-files contract that its endpoint never sends.
   *
   *  `null` means "we cannot preview this one" and is rendered as a state INSIDE this modal, not routed
   *  elsewhere — so every file type has the same presentation and the same actions. */
  kind: DocViewerKind | null;
  /** The BI page_name the viewer's own actions report under. */
  pageName: string;
  /** This file's own page as a new-tab target, when the opener could name one — see `DocViewerRequest`. */
  fileHref?: string;
  /** The workspace the file belongs to, for the subtitle's third segment. Only the surfaces that know it
   *  pass it (the workspace gallery); absent it, the segment is omitted rather than fabricated. */
  workspaceName?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const agent = useAgentName();
  const { logModalActionBi } = useGenericBi();
  const sizeBytes = useFileSize(file);

  const modalBi = (action: string) => logModalActionBi({ page_name: pageName, modal_name: 'doc_viewer', action, answer: file.name });
  const close = () => {
    modalBi('close');
    onClose();
  };
  const discuss = () => {
    modalBi('discuss');
    const link = chatHandoffLink({ kind: 'new', message: t('agentCreations.detail.askMessage', { file: file.name }) }, basePath);
    void router.push(link.route, link.path);
  };

  // `onPrint` does NOT log BI itself — `FileActionButton` already fires `modalBi('print')` for the footer
  // entry. Discuss is the exception: it is rendered as the right-hand primary below (not via
  // `FileActionButton`), so its own handler logs.
  // `printDocument` prints our own page unless the view on screen claimed the job — a PDF does, because the
  // parent print job cannot paginate its embed. See `lib/doc-print.ts`.
  const callbacks: FileActionCallbacks = { onDiscuss: discuss, onPrint: printDocument };
  // The utility group (Download / Print / Open in new tab, or the wixel Open/Edit/Download trio) goes on the
  // left of the footer; Discuss is the right-hand primary, so the builder's own Discuss group is unused.
  const [utilityActions] = buildFileActions(file, kind, callbacks, { context: 'viewer', t, agentName: agent.short, fileHref });

  return (
    <Modal
      open
      onClose={close}
      title={file.name}
      description={fileSubtitle(file, t, workspaceName)}
      labelledBy="doc-viewer-title"
      size="maximum"
      headerSeparator
      footerSeparator
      cardClassName={styles.card}
      contentClassName={styles.modalContent}
      footer={
        <div className={styles.footerBar}>
          <div className={styles.footerGroup}>
            {utilityActions.map((action) => (
              <FileActionButton key={action.key} action={action} onBi={modalBi} />
            ))}
          </div>
          <div className={styles.footerGroup}>
            {/* No Close button. The modal already closes three ways — the header ✕, Escape and a scrim click —
                and a fourth in the footer only competed with Discuss for the eye at the corner where the
                primary action belongs. */}
            <Button onClick={discuss}>{t('agentCreations.detail.discuss', { agentName: agent.short })}</Button>
          </div>
        </div>
      }
    >
      <div className={styles.layout}>
        {/* `orion-doc-viewer-body` is a plain global marker (not a CSS-module class) — see globals.css for
            the print-only rule it hooks into `@media print`. The per-kind fit modifier lives in
            `docViewerBodyFit`, shared with the standalone page so the two hosts cannot drift. */}
        <div className={`${styles.body} orion-doc-viewer-body ${BODY_FIT[docViewerBodyFit(kind)]}`}>
          <DocViewerBody file={file} kind={kind} onBi={modalBi} />
        </div>
      </div>
      <div className={styles.metaBar}>
        <FileMetaLine item={file} sizeBytes={sizeBytes} />
      </div>
    </Modal>
  );
}
